import React, { useEffect, useState } from 'react'
import { FaSave, FaSpinner, FaFlask, FaDatabase } from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, Toast, useToast } from '../components/ui'
import { useAuth } from '../context/AuthContext'
import { SHOW_STOCK } from '../lib/features'
import * as api from '../lib/api'

/**
 * Shop settings — the figures that used to be hard-coded in CheckoutPage.
 *
 * Each row renders from the value's own JSON type rather than from a per-key
 * switch: booleans get a checkbox, numbers a number field, strings a text box.
 * That means a setting added to the SQL later shows up here with a usable control
 * and no change to this file.
 *
 * Two optional columns refine that without breaking it. `group_label` sorts the
 * settings into cards, so invoicing's dozen entries do not turn this page into a
 * wall of inputs; `input_kind` can ask for a textarea, which banking details and
 * a multi-line address genuinely need. Both may be null — a setting written by a
 * future migration that sets neither still appears, in the Shop card, with the
 * control its JSON type implies.
 */
// Settings that belong to a switched-off feature. The row stays in the database
// with its value intact, so turning the feature back on brings the control back
// exactly as it was left. See lib/features.js.
const HIDDEN_KEYS = SHOW_STOCK ? [] : ['low_stock_threshold']

const SettingsPage = () => {
  const { profile, role, isDemo, canManage } = useAuth()
  const [settings, setSettings] = useState([])
  const [draft, setDraft] = useState({})
  const [state, setState] = useState({ loading: true, error: null })
  const [saving, setSaving] = useState(false)
  const { toast, notify, notifyError, dismiss } = useToast()

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const all = await api.getSettings()
        if (cancelled) return
        const rows = (all ?? []).filter((r) => !HIDDEN_KEYS.includes(r.key))
        setSettings(rows)
        setDraft(Object.fromEntries(rows.map((r) => [r.key, r.value])))
        setState({ loading: false, error: null })
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const dirty = settings.filter((s) => JSON.stringify(draft[s.key]) !== JSON.stringify(s.value))

  // Insertion order, not alphabetical: the migrations write Shop first and it
  // should stay first, and a group added later belongs at the bottom rather than
  // jumping the queue because its name starts with A.
  const groups = settings.reduce((map, setting) => {
    const name = setting.group_label || 'Shop'
    if (!map.has(name)) map.set(name, [])
    map.get(name).push(setting)
    return map
  }, new Map())

  const GROUP_BLURB = {
    Shop: 'Applies to the storefront immediately after saving',
    Invoicing: 'Printed on every invoice, quote and credit note',
  }

  const saveAll = async () => {
    setSaving(true)
    try {
      // Sequential rather than Promise.all: there are at most a handful, and a
      // partial failure is far easier to reason about when the order is known.
      for (const setting of dirty) {
        await api.saveSetting(setting.key, draft[setting.key])
      }
      notify(`${dirty.length} setting${dirty.length === 1 ? '' : 's'} saved.`)
      setSettings((current) => current.map((s) => ({ ...s, value: draft[s.key] })))
    } catch (err) {
      notifyError(err)
    } finally {
      setSaving(false)
    }
  }

  if (state.loading) return <Spinner label="Loading settings…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load settings.</strong> {state.error.message}</Banner>
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Settings" subtitle="Shop-wide options and your account">
        {canManage && dirty.length > 0 && (
          <button onClick={saveAll} disabled={saving} className="btn-primary">
            {saving ? <FaSpinner className="animate-spin" /> : <FaSave className="text-[11px]" />}
            Save {dirty.length} change{dirty.length === 1 ? '' : 's'}
          </button>
        )}
      </PageHeader>

      {isDemo && (
        <Banner tone="warning">
          <strong>Demo mode.</strong> These controls are live but saving is disabled until Supabase is
          connected. The figures shown are the defaults written by the migrations.
        </Banner>
      )}

      {!canManage && (
        <Banner tone="info">
          Shop settings are changed by the owner or a manager. You can see them here.
        </Banner>
      )}

      {/* Disabled for staff: store_settings writes need is_manager. */}
      <fieldset disabled={!canManage} className="m-0 min-w-0 space-y-5 border-0 p-0">
      {[...groups.entries()].map(([name, rows]) => (
        <Card key={name} title={name} subtitle={GROUP_BLURB[name]}>
          <div className="divide-y divide-hairline/70">
            {rows.map((setting) => {
              const multiline = setting.input_kind === 'textarea'
              return (
                <div
                  key={setting.key}
                  className="flex flex-wrap items-start justify-between gap-4 py-3.5 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <label className="block text-[13px] font-medium text-ink" htmlFor={setting.key}>
                      {setting.label ?? setting.key}
                    </label>
                    {setting.description && (
                      <p className="mt-0.5 max-w-xl text-2xs text-ink-muted">{setting.description}</p>
                    )}
                  </div>

                  {/* A textarea needs room to be usable; everything else is a
                      short value and looks lost in a wide box. */}
                  <div className={`w-full shrink-0 ${multiline ? 'sm:w-80' : 'sm:w-48'}`}>
                    {typeof setting.value === 'boolean' ? (
                      <label className="flex items-center gap-2.5 text-[13px]">
                        <input
                          id={setting.key}
                          type="checkbox"
                          checked={Boolean(draft[setting.key])}
                          onChange={(e) => setDraft({ ...draft, [setting.key]: e.target.checked })}
                          className="h-4 w-4 rounded border-hairline text-brand-navy focus:ring-brand-navy/30"
                        />
                        {draft[setting.key] ? 'On' : 'Off'}
                      </label>
                    ) : typeof setting.value === 'number' ? (
                      <input
                        id={setting.key}
                        type="number"
                        step="0.01"
                        min="0"
                        className="input text-right"
                        value={draft[setting.key] ?? 0}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            // Empty must not become NaN, which Postgres rejects as
                            // invalid JSON and which renders as "NaN" in the field.
                            [setting.key]: e.target.value === '' ? 0 : Number(e.target.value),
                          })
                        }
                      />
                    ) : multiline ? (
                      <textarea
                        id={setting.key}
                        rows={4}
                        className="input font-[inherit]"
                        value={draft[setting.key] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [setting.key]: e.target.value })}
                      />
                    ) : (
                      <input
                        id={setting.key}
                        className="input"
                        value={draft[setting.key] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [setting.key]: e.target.value })}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      ))}
      </fieldset>

      <Card title="Your account">
        <dl className="grid gap-3 sm:grid-cols-3">
          <div>
            <dt className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">Name</dt>
            <dd className="mt-0.5 text-[13px] text-ink">{profile?.full_name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">Email</dt>
            <dd className="mt-0.5 truncate text-[13px] text-ink">{profile?.email}</dd>
          </div>
          <div>
            <dt className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">Role</dt>
            <dd className="mt-0.5 text-[13px] capitalize text-ink">{role}</dd>
          </div>
        </dl>

        <p className="mt-4 text-2xs leading-relaxed text-ink-muted">
          People are invited from <strong>Team</strong>. Owners and managers look after the
          catalogue, prices, settings and the team — managers for staff and managers only. Staff work
          orders{SHOW_STOCK ? ' and adjust stock' : ''}.
        </p>
      </Card>

      <Card title="Connection">
        <div className="flex items-start gap-2.5">
          {isDemo ? (
            <>
              <FaFlask className="mt-0.5 shrink-0 text-[12px] text-status-warning" aria-hidden="true" />
              <div className="text-[13px]">
                <div className="font-medium text-ink">Demo mode — no database connected</div>
                <p className="mt-0.5 text-2xs text-ink-muted">
                  Set <code className="font-mono">VITE_SUPABASE_URL</code> and{' '}
                  <code className="font-mono">VITE_SUPABASE_ANON_KEY</code> in{' '}
                  <code className="font-mono">apps/admin/.env</code>, then restart the dev server.
                </p>
              </div>
            </>
          ) : (
            <>
              <FaDatabase className="mt-0.5 shrink-0 text-[12px] text-status-good" aria-hidden="true" />
              <div className="text-[13px]">
                <div className="font-medium text-ink">Connected to Supabase</div>
                <p className="mt-0.5 break-all font-mono text-2xs text-ink-muted">
                  {import.meta.env.VITE_SUPABASE_URL}
                </p>
              </div>
            </>
          )}
        </div>
      </Card>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

export default SettingsPage
